<!-- 電話番号・メールアドレス・コメント入力スライド -->
<script setup lang="ts">
import { ref, computed } from 'vue';

import type Inquiry from '@/javascripts/utils/inquiry';
import type { InquiryValidation } from '@/javascripts/composables/inquiry/validation';

// コンポーネント
import TermOfServiceModal from '@/javascripts/common/kiyaku.vue'
import PrivacyPolicyModal from '@/javascripts/common/privacy_policy_modal.vue'

/** プロパティ */
const props = defineProps<{
  /** 問い合わせオブジェクト */
  phone: Inquiry['phone'],
  email: Inquiry['email'],
  comment: Inquiry['comment'],
  /** 問い合わせバリデーション */
  v$: InquiryValidation
}>();

/** カスタムイベント */
const emit = defineEmits([
  'back', 
  'update:phone', 
  'update:email', 
  'update:comment', 
  'submit'
]);

/** フォーム項目の computed プロパティ */
const localPhone = computed({
  get: () => props.phone,
  set: (value) => emit('update:phone', value)
});
const localEmail = computed({
  get: () => props.email,
  set: (value) => emit('update:email', value)
});
const localComment = computed({
  get: () => props.comment,
  set: (value) => emit('update:comment', value)
});

/** 利用規約モーダルコンポーネント */
const termOfServiceModal = ref<InstanceType<typeof TermOfServiceModal> | null>();
/** 個人情報保護方針モーダルコンポーネント */
const privacyPolicyModal = ref<InstanceType<typeof PrivacyPolicyModal> | null>();

/** 
 * コメント入力フォームのトグルスイッチ
 * ※デフォルトはfalse、コメント入力済みの場合はtrue
 */
const showComment = ref(Boolean(props.comment));

/** 電話番号・メールアドレス・コメントのバリデーション結果 */
const invalid = computed(() =>
  props.v$.phone.$invalid || props.v$.email.$invalid || props.v$.comment.$invalid
);
</script>

<template>
  <div class="slide_body">
    <TermOfServiceModal ref="termOfServiceModal" />
    <PrivacyPolicyModal ref="privacyPolicyModal" />
    <div class="option_wrap">
      <h3 class="option_title required">電話番号</h3>
      <div class="option_group mb16">
        <div class="option_input option_input_max">
          <input
            id="phone"
            v-model="localPhone"
            class="w-100p"
            type="tel"
            name="phone"
            placeholder="0901111111"
            required
          >
        </div>
      </div>
      <div
        v-for="(error, key) in v$.phone.$errors"
        :key="key"
        class="validation_message_box"
      >{{ error.$message }}</div>          
      <h3 class="option_title">メールアドレス</h3>
      <div class="option_group mb16">
        <div class="option_input option_input_max">
          <input
            id="email"
            v-model="localEmail"
            class="w-100p"
            type="email"
            name="email"
            placeholder="abc@example.com"
          >
        </div>
      </div>
      <div
        v-for="(error, key) in v$.email.$errors"
        :key="key"
        class="validation_message_box"
      >{{ error.$message }}</div>         
      
      <a @click="showComment = !showComment">
        <label for="comment" class="comment_toggle">
          {{ showComment ? '- ' : '+ ' }}ご希望・ご質問を入力する
        </label>
      </a>
      
      <div v-if="showComment" class="option_group">
        <div class="option_input option_input_max">
          <textarea
            id="comment"
            v-model="localComment"
            name="comment"
            class="fv-textarea"
            placeholder="400文字以内でご希望・ご質問内容をご記入ください。"
          />
        </div>
      </div>
      <div
        v-for="(error, key) in v$.comment.$errors"
        :key="key"
        class="validation_message_box"
      >{{ error.$message }}</div>
    </div>

    <div class="option_annotation_box">
      <a class="open_modal_link" @click="termOfServiceModal?.openModal()">利用規約</a>、<a class="open_modal_link" @click="privacyPolicyModal?.openModal()">個人情報保護方針</a>に同意し、
    </div>
    
    <div class="slide_control_group">
      <a
        id="ph_back_4"
        class="back_link"
        @click="emit('back')"
      >&lt; 戻る</a>
      <button
        id="gtm_top_submit_button"
        :disabled="invalid"
        class="form_button_last"
        @click.once="emit('submit')"
      >
        <span>無料</span>登録する
      </button>
    </div>
  </div>
</template>